import { useState } from "react";
import { PencilIcon, TrashIcon } from "@heroicons/react/20/solid";

import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { Button } from "../../../../../common/Atoms/Button";
import { Modal } from "../../../../../common/Atoms/Modal";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { TagColorPicker, randomTagColor } from "../../../Molecules/TagColorPicker";
import { useInvoiceTags } from "../../../../hooks/useInvoiceTags";
import { useContactTags } from "../../../../hooks/useContactTags";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import {
  useCreateInvoiceTagMutation,
  useDeleteInvoiceTagMutation,
  useUpdateInvoiceTagMutation,
} from "../../../../services/api/invoiceTagsApi/invoiceTags";
import {
  useCreateContactTagMutation,
  useDeleteContactTagMutation,
  useUpdateContactTagMutation,
} from "../../../../services/api/contactTagsApi/contactTags";

interface ITagItem {
  name: string;
  color: string | null;
  count: number;
}

interface ITagManagerProps {
  tags: ITagItem[];
  isLoading: boolean;
  /** What the tag applies to, e.g. "invoice" | "contact" */
  noun: string;
  onCreate: (tagName: string, color: string) => Promise<unknown>;
  onUpdateColor: (tagName: string, color: string) => void;
  onDelete: (tagName: string) => void;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? `` : `s`}`;
}

function TagManager({
  tags,
  isLoading,
  noun,
  onCreate,
  onUpdateColor,
  onDelete,
  isCreating,
  isUpdating,
  isDeleting,
}: ITagManagerProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<ITagItem | null>(null);
  const [name, setName] = useState(``);
  const [color, setColor] = useState(randomTagColor);

  function openCreate() {
    setEditingTag(null);
    setName(``);
    setColor(randomTagColor());
    setModalOpen(true);
  }

  function openEdit(tag: ITagItem) {
    setEditingTag(tag);
    setName(tag.name);
    setColor(tag.color || randomTagColor());
    setModalOpen(true);
  }

  async function save() {
    if (editingTag) {
      onUpdateColor(editingTag.name, color);
      setModalOpen(false);

      return;
    }

    if (!name) {
      return;
    }

    const res = await onCreate(name, color);

    if (!Object.keys(res || {}).includes(`error`)) {
      setModalOpen(false);
    }
  }

  function deleteFromModal() {
    if (editingTag) {
      onDelete(editingTag.name);
    }

    setModalOpen(false);
  }

  return (
    <div className={ `flex flex-col gap-2.5` }>
      <div className={ `flex items-center justify-between` }>
        <span className={ `text-xs text-gray-500` }>
          { plural(tags.length, `tag`) }
        </span>
        <Button
          color={ `paidniceGreenOutline` }
          size={ `sm` }
          onClick={ openCreate }
        >
          { `Add tag` }
        </Button>
      </div>

      { !tags.length && !isLoading && (
        <div className={ `flex flex-col items-center justify-center gap-2 rounded-lg border-[1.5px] border-dashed border-gray-200 p-6 text-center` }>
          <Paragraph variant={ `help` }>
            { `No ${noun} tags yet` }
          </Paragraph>
        </div>
      ) }

      { !!tags.length && (
        <div className={ `overflow-hidden rounded-lg border border-gray-200` }>
          { tags.map(tag => (
            <div
              key={ tag.name }
              className={ `flex items-center gap-3 border-t border-gray-100 bg-white px-4 py-2.5 first:border-t-0 hover:bg-gray-50` }
            >
              <span
                className={ `h-4 w-4 shrink-0 rounded-[5px] ${tag.color ? `` : `bg-gray-200`}` }
                style={ tag.color ? { backgroundColor: tag.color } : undefined }
              />
              <span className={ `text-sm font-medium text-gray-900` }>
                { tag.name }
              </span>
              <span className={ `text-xs text-gray-400` }>
                { plural(tag.count, noun) }
              </span>
              <span className={ `flex-1` } />
              <button
                type={ `button` }
                title={ `Edit` }
                aria-label={ `Edit tag ${tag.name}` }
                onClick={ () => openEdit(tag) }
                className={ `flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700` }
              >
                <PencilIcon className={ `h-3.5 w-3.5` } />
              </button>
              <button
                type={ `button` }
                title={ `Delete` }
                aria-label={ `Delete tag ${tag.name}` }
                onClick={ () => onDelete(tag.name) }
                disabled={ isDeleting }
                className={ `flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-700` }
              >
                <TrashIcon className={ `h-3.5 w-3.5` } />
              </button>
            </div>
          )) }
        </div>
      ) }

      <Modal
        open={ modalOpen }
        setOpen={ setModalOpen }
        title={ editingTag ? `Edit tag` : `New ${noun} tag` }
      >
        <div className={ `flex flex-col gap-4` }>
          <InputTextAddon
            value={ name }
            onChange={ e => setName(e.value.toString()) }
            placeholder={ noun === `contact` ? `e.g. VIP` : `e.g. Priority` }
            label={ `Tag name` }
            disabled={ !!editingTag }
          />
          <div>
            <Paragraph className={ `mb-2` }>
              { `Colour` }
            </Paragraph>
            <TagColorPicker
              value={ color }
              onChange={ setColor }
            />
          </div>
          { editingTag && (
            <Paragraph variant={ `help` }>
              { `Applied to ${plural(editingTag.count, noun)}.` }
            </Paragraph>
          ) }
          <div className={ `mt-1 flex items-center justify-between gap-2` }>
            { editingTag ? (
              <Button
                color={ `paidniceRedOutline` }
                onClick={ deleteFromModal }
                loading={ isDeleting }
              >
                { `Delete tag` }
              </Button>
            ) : <span /> }
            <div className={ `flex gap-2` }>
              <Button
                color={ `white` }
                onClick={ () => setModalOpen(false) }
              >
                { `Cancel` }
              </Button>
              <Button
                onClick={ save }
                loading={ editingTag ? isUpdating : isCreating }
                disabled={ !editingTag && !name }
              >
                { editingTag ? `Save changes` : `Add tag` }
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function TagsSettings() {
  const currentOrg = useGetSelectedOrganisation();
  const invoiceTags = useInvoiceTags();
  const contactTags = useContactTags();
  const [createInvoiceTag, { isLoading: creatingInvoiceTag }] = useCreateInvoiceTagMutation();
  const [updateInvoiceTag, { isLoading: updatingInvoiceTag }] = useUpdateInvoiceTagMutation();
  const [deleteInvoiceTag, { isLoading: deletingInvoiceTag }] = useDeleteInvoiceTagMutation();
  const [createContactTag, { isLoading: creatingContactTag }] = useCreateContactTagMutation();
  const [updateContactTag, { isLoading: updatingContactTag }] = useUpdateContactTagMutation();
  const [deleteContactTag, { isLoading: deletingContactTag }] = useDeleteContactTagMutation();

  return (
    <>
      <TwoColFormRow
        heading={ `Invoice Tags` }
        description={ `Tags can be applied to invoices to categorise them. Deleting a tag removes it from all invoices.` }
      >
        <TagManager
          tags={ invoiceTags.data?.tags || [] }
          isLoading={ invoiceTags.isLoading }
          noun={ `invoice` }
          onCreate={ (tagName, color) => createInvoiceTag({
            organisationId: currentOrg.id,
            tagName,
            selectedInvoices: [],
            color,
          }) }
          onUpdateColor={ (tagName, color) => updateInvoiceTag({
            organisationId: currentOrg.id,
            tagName,
            color,
          }) }
          onDelete={ tagName => deleteInvoiceTag({
            organisationId: currentOrg.id,
            tagName,
          }) }
          isCreating={ creatingInvoiceTag }
          isUpdating={ updatingInvoiceTag }
          isDeleting={ deletingInvoiceTag }
        />
      </TwoColFormRow>

      <TwoColFormRow
        heading={ `Contact Tags` }
        description={ `Tags can be applied to contacts and used to include or exclude contacts in policies. Deleting a tag removes it from all contacts.` }
      >
        <TagManager
          tags={ contactTags.data?.tags || [] }
          isLoading={ contactTags.isLoading }
          noun={ `contact` }
          onCreate={ (tagName, color) => createContactTag({
            organisationId: currentOrg.id,
            tagName,
            selectedContacts: [],
            color,
          }) }
          onUpdateColor={ (tagName, color) => updateContactTag({
            organisationId: currentOrg.id,
            tagName,
            color,
          }) }
          onDelete={ tagName => deleteContactTag({
            organisationId: currentOrg.id,
            tagName,
          }) }
          isCreating={ creatingContactTag }
          isUpdating={ updatingContactTag }
          isDeleting={ deletingContactTag }
        />
      </TwoColFormRow>
    </>
  );
}
